import React, { useState } from "react";
import { Button, Input, notification } from "antd";
import { DislikeOutlined, LikeOutlined, SmileOutlined } from "@ant-design/icons";
import { sendArticleFeedback } from "./helpCenterApi";
import { readHelpValue, writeHelpValue } from "./helpStorage";

// The vote itself is kept by the backend, one per user and article. This is
// only a note to this browser, so an article the user already voted on doesn't
// ask again; a browser that has forgotten just asks once more, and the new vote
// replaces the old one.
const VOTES_KEY = "articleVotes";

const readVotes = () => {
   const votes = readHelpValue(VOTES_KEY, {});
   return votes && typeof votes === "object" ? votes : {};
};

/**
 * "Was this article helpful?" at the end of an article. A "No" asks what was
 * missing before it is sent. Once sent, the article shows a thank-you here.
 */
const ArticleFeedback = ({ articleId }) => {
   const [step, setStep] = useState(() => (readVotes()[articleId] ? "thanks" : "ask"));
   const [comment, setComment] = useState("");
   const [isSending, setIsSending] = useState(false);

   const send = async (helpful) => {
      setIsSending(true);
      try {
         await sendArticleFeedback({
            articleId,
            helpful,
            comment: helpful ? null : comment.trim() || null,
            page: window.location.pathname
         });
         writeHelpValue(VOTES_KEY, { ...readVotes(), [articleId]: helpful ? "yes" : "no" });
         setStep("thanks");
      } catch (error) {
         notification.error({
            message: "Oops! A problem has occurred!",
            description: "We couldn't send your feedback, please try again."
         });
      } finally {
         setIsSending(false);
      }
   };

   if (step === "thanks") {
      return (
         <div className="help-feedback help-feedback--done">
            <SmileOutlined />
            <span>Thanks for your feedback!</span>
         </div>
      );
   }

   if (step === "comment") {
      return (
         <div className="help-feedback help-feedback--comment">
            <label htmlFor={`help-feedback-${articleId}`} className="help-feedback__question">
               What were you looking for?
            </label>
            <Input.TextArea
               id={`help-feedback-${articleId}`}
               value={comment}
               onChange={(event) => setComment(event.target.value)}
               placeholder="Optional: tell us what was missing or unclear"
               autoSize={{ minRows: 2, maxRows: 5 }}
               maxLength={500}
               autoFocus
            />
            <div className="help-feedback__actions">
               <Button type="text" size="small" onClick={() => setStep("ask")} disabled={isSending}>
                  Cancel
               </Button>
               <Button type="primary" size="small" onClick={() => send(false)} loading={isSending}>
                  Send feedback
               </Button>
            </div>
         </div>
      );
   }

   return (
      <div className="help-feedback">
         <span className="help-feedback__question">Was this article helpful?</span>
         <div className="help-feedback__actions">
            <Button
               size="small"
               icon={<LikeOutlined />}
               onClick={() => send(true)}
               loading={isSending}
            >
               Yes
            </Button>
            <Button
               size="small"
               icon={<DislikeOutlined />}
               onClick={() => setStep("comment")}
               disabled={isSending}
            >
               No
            </Button>
         </div>
      </div>
   );
};

export default ArticleFeedback;
